<template>
	<section class="calculator-section" id="calculator">
		<h3 class="h-text">
			<span>{{ 'Рассчитать\n' }}</span>
			<span class="h-text2">стоимость доставки</span>
		</h3>

		<div class="calculator-row">
			<div class="calculator">
				<h4 ref="formTitleHeader">Информация о грузе</h4>

				<ShAccordion
					v-for="(item, idx) in containers"
					:key="item._meta.id"
					v-model="item._meta.visible"
				>
					<template #header="{ toggle, visible }">
						<div class="container-header">
							<UIShButtonLabel class="btn-toggle" @click="toggle">
								<template #icon-left>
									<UIShIcon
										name="chevron-down"
										:class="{
											icon: true,
											'icon-rotate': visible
										}"
									/>
								</template>

								<span class="name">
									Грузовое место {{ idx + 1 }}
								</span>
							</UIShButtonLabel>

							<UIShButtonLabel
								v-if="containers.length > 1"
								class="btn-del"
								label="Удалить"
								icon-right="trash"
								sm
								:md="1920"
								@click="deleteId = item._meta.id"
							/>
						</div>
					</template>

					<template #body>
						<CalculatorForm
							ref="containersForm"
							v-model="containers[idx]"
							class="container-body"
						/>
					</template>
				</ShAccordion>

				<UIShButton
					class="add-btn"
					label="Добавить груз"
					kind="tertiary-gray"
					icon-left="plus"
					fill
					lg
					:xl="1920"
					@click="handleAddContainer"
				/>

				<!-- <CustomsBlock
					ref="customsForm"
					:containers-id="containersId"
					v-model="containersCargoData"
					v-model:is-clearance="isClearance"
				/> -->
			</div>

			<CalculatorTotal
				:containers
				:containersCargoData
				:is-clearance
				v-model:isInsure="isInsure"
				v-model:files="files"
				@submit="handleDisplaySubmitModal"
				@result="$emit('result', $event)"
			/>
		</div>

		<DeleteConfirmModal v-if="deleteId" @confirm="handleDeleteConfirm" />

		<CalculatorSubmitModal
			v-if="dSubmitModal"
			v-model="submitFeedbackData"
			@submit="handleSubmitCalculation"
			@cancel="dSubmitModal = false"
		>
			<p
				v-if="submitResult.submitted"
				:class="[submitResult.success ? 'success' : 'error']"
			>
				{{ submitResult.message }}
			</p>
		</CalculatorSubmitModal>
	</section>
</template>

<script setup lang="ts">
import { getRandomId } from '@/utils';
import type {
	ContainerData,
	ContainerBlocks,
	FeedbackData,
	CalcPayload,
	UploadFileType,
	ApiDeliveriesResult
} from '@/common/types';

// import CustomsBlock from '@/components/calculator/CustomsBlock.vue';
import CalculatorForm from '@/components/calculator/CalculatorForm.vue';
import CalculatorTotal from '@/components/calculator/CalculatorTotal.vue';
import DeleteConfirmModal from '@/components/calculator/DeleteConfirmModal.vue';
import CalculatorSubmitModal from '@/components/calculator/CalculatorSubmitModal.vue';

const $emit = defineEmits<{
	(e: 'result', d: ApiDeliveriesResult): void;
}>();

const deleteId = ref<string | undefined>();
const isInsure = ref(false);
const isClearance = ref(false);
const dSubmitModal = ref(false);
const submitResult = ref({ submitted: false, success: false, message: '' });

const files = ref<UploadFileType[]>([]);
const containers = ref<ContainerData[]>([]);
const submitFeedbackData = ref<Partial<FeedbackData>>({});
const containersCargoData = ref<ContainerBlocks[]>([]);
const containersForm =
	useTemplateRef<Array<InstanceType<typeof CalculatorForm>>>(
		'containersForm'
	);

// const customsForm = useTemplateRef('customsForm');
const formTitleHeader = useTemplateRef('formTitleHeader');

const { postSubmitCalculator } = useCalcSubmit();
const calcStorage = useCalculatorStorage();

const router = useRouter();

onMounted(async () => {
	const storageValues = calcStorage.getStorageValues();

	if (storageValues !== null) {
		isInsure.value = storageValues.isInsure ?? false;

		containers.value = storageValues.items.map((v) => ({
			...getNewContainer(),
			...v
		}));

		await calcStorage.resetQuery();

		return;
	}

	containers.value = [getNewContainer()];
});

function getNewContainer() {
	return { _meta: { id: getRandomId(), visible: true } };
}

function handleAddContainer() {
	containers.value.push(getNewContainer());
}

function handleDeleteContainer(id: string) {
	containers.value = containers.value.filter((v) => v._meta.id !== id);
}

function handleDeleteConfirm(confirmed: boolean) {
	if (confirmed && deleteId.value) {
		handleDeleteContainer(deleteId.value);
	}

	deleteId.value = undefined;
}

function handleResetAllForms() {
	files.value.forEach((f) => {
		URL.revokeObjectURL(f.src);
	});

	files.value = [];
}

async function validateAllForms(): Promise<boolean> {
	if (!containersForm.value?.length) return false;

	const invalidForm = await Promise.all(
		containersForm.value.map((f) => f.validate())
	).then((r) => r.some((f) => !f.valid));

	if (invalidForm) return false;

	// if (isClearance.value && customsForm.value) {
	// 	const validCustoms = await customsForm.value.validate();
	// 	if (!validCustoms) return false;
	// }

	return true;
}

async function handleDisplaySubmitModal() {
	const isValidAll = await validateAllForms();
	if (!isValidAll) {
		const invalidNode = document.querySelector('.input-row.invalid');
		const targetScrollNode = invalidNode ?? formTitleHeader.value;

		targetScrollNode?.scrollIntoView({
			block: 'center',
			inline: 'center',
			behavior: 'smooth'
		});
	}
	dSubmitModal.value = isValidAll;
}

async function handleSubmitCalculation() {
	if (!submitFeedbackData.value) return;

	const payload: CalcPayload = {
		type: 'train',
		isClearance: isClearance.value,
		isInsure: isInsure.value,
		cargos: containers.value.map((c) => {
			const goods =
				containersCargoData.value.find((v) => v.id === c._meta.id)
					?.blocks ?? [];

			return {
				height: c.height,
				width: c.width,
				length: c.length,
				weight: c.weight,
				count: c.count
				// goods: goods
				// 	.filter((g) => g.name)
				// 	.map((v) => {
				// 		return {
				// 			name: v.name,
				// 			price: v.price,
				// 			category: v.category,
				// 			currency: v.currency
				// 		};
				// 	})
			};
		})
	};

	const feedbackData = submitFeedbackData.value as Required<FeedbackData>;

	const response = await postSubmitCalculator(
		feedbackData,
		payload,
		files.value
	);

	submitResult.value.submitted = true;
	submitResult.value.success = response.success;
	submitResult.value.message = response.message;

	setTimeout(() => {
		submitResult.value.submitted = false;
		dSubmitModal.value = false;
		router.push({ path: '/', force: true });
		handleResetAllForms();
	}, 3000);
}
</script>

<style lang="scss" scoped>
.calculator-section {
	width: 100%;
	position: relative;
	margin-top: 4.444rem;
}

.h-text {
	@include useFont(dxsb);
	text-transform: uppercase;
	color: var(--color-white);
	white-space: pre-line;

	&2 {
		color: var(--color-gray300);
	}
}

.calculator-row {
	display: flex;
	flex-direction: column;
	gap: 0.667rem;
	margin-top: 1.333rem;
}

.calculator {
	padding: 0.889rem;
	border-radius: var(--border-radius);
	background-color: var(--color-white);
	display: flex;
	flex-direction: column;
	gap: 0.889rem;

	h4 {
		color: var(--color-gray950);
		@include useFont(tlgs);
	}
}

.container-header {
	display: flex;
	align-items: center;
	flex-wrap: nowrap;
	gap: 0.444rem;
	justify-content: space-between;

	.btn-toggle {
		gap: 0.444rem;
		color: var(--color-gray600);
		justify-content: flex-start;

		.name {
			@include useFont(tmdb);
			color: var(--color-gray950);
		}

		.icon {
			transition: transform 0.2s;
		}

		.icon-rotate {
			transform: rotate(180deg);
		}
	}

	.btn-del {
		gap: 0.222rem;
	}
}

.container-body {
	padding: 0.889rem;
	padding-top: 0;
}

.error {
	@include useFont(tsmm);
	color: var(--color-error600);
}

.success {
	@include useFont(tsmm);
	color: var(--color-success700);
}

@include screen1440 {
	.calculator-row {
		display: grid;
		grid-template-columns: minmax(27rem, 57rem) minmax(27rem, 1fr);
		grid-template-rows: auto;
		align-items: flex-start;
	}

	.calculator {
		height: 100%;

		.add-btn {
			margin-top: auto;
		}
	}
}

@include screen1920 {
	.calculator-section {
		margin-top: 7.778rem;
	}

	.h-text {
		@include useFont(dxlb);
	}

	.calculator-row {
		display: grid;
		column-gap: 1.111rem;
		margin-top: 2.667rem;
		grid-template-columns: minmax(44.444rem, 67.778rem) 1fr;
		grid-template-rows: auto;
		align-items: flex-start;
	}

	.calculator {
		padding: 1.778rem;
		gap: 1.111rem;

		h4 {
			@include useFont(dxss);
			padding-bottom: 0.667rem;
		}
	}

	.container-header {
		gap: 0.667rem;

		.btn-toggle {
			gap: 0.667rem;

			.name {
				@include useFont(txlb);
			}
		}
	}

	.container-body {
		padding: 1.111rem;
	}
}
</style>
